import { Coordinate } from "ol/coordinate";
import { Point, Polygon } from "ol/geom";
import { fromLonLat } from "ol/proj";
import { Fill, Icon, Stroke, Style, Text } from "ol/style";
import { SPACECRAFT_METADATA } from "./common";
import { circular } from "ol/geom/Polygon";
import CircleStyle from "ol/style/Circle";

export const MCC_ELEMENTS: {[key: string]: {
  name: string,
  geometry: Point | Polygon,
  description?: string,
  style: Style
}} = {
  "sun": {
    name: "Sun",
    geometry: new Point([0, 0]),
    style: new Style({
      image: new Icon({
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        src: 'img/icons/issmccmap/sun.svg',
        scale: 0.025
      }),
      text: new Text({
        text: ["SUN", "bold 15px sans-serif"],
        offsetY: 20,
        offsetX: 1,
        fill: new Fill({color: 'yellow'}),
      })
    })
  },
  "saa": {
    name: "SAA",
    description: "South Atlantic Anomaly Zone",
    geometry: new Polygon([[
      [-45, -50],
      [4, -50],
      [20, -45],
      [28, -40],
      [29.8, -38],
      [30, -36],
      [28, -34],
      [23.5, -30.5],
      [21, -29],
      [-42, -15],
      [-45, -14.9],
      [-49, -14.9],
      [-53, -15],
      [-63, -21],
      [-66, -25],
      [-66, -31],
      [-64, -37],
      [-54, -45],
      [-45, -50],
      ].map(c => fromLonLat(c))] as unknown as Coordinate[][]),
    style: new Style({
      stroke: new Stroke({
        width: 1.5,
        color: "grey"
      }),
      text: new Text({
        text: [
          "SAA", `bold 15px sans-serif`,
          '\n', '',
          "South Atlantic Anomaly Zone", `normal 10px sans-serif`
        ],
        offsetX: -5,
        offsetY: 10,
        fill: new Fill({color: 'grey'})
      })
    })
  },
  "TDRS 7": {
    name: "TDRS 7",
    description: "T275W",
    geometry: new Point([0, 0]),
    style: new Style({
      image: new Icon({
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        src: `img/icons/issmccmap/${SPACECRAFT_METADATA.find(scMeta => "TDRS 7".match(scMeta.match))?.icon.name}`,
        scale: SPACECRAFT_METADATA.find(scMeta => "TDRS 7".match(scMeta.match))?.icon.scale
      }),
      text: new Text({
        text: ["T275W", "bold 15px sans-serif"],
        offsetY: 25,
        fill: new Fill({color: 'cyan'}),
      })
    })
  },
  "TDRS 10": {
    name: "TDRS 10",
    description: "T171W",
    geometry: new Point([0, 0]),
    style: new Style({
      image: new Icon({
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        src: `img/icons/issmccmap/${SPACECRAFT_METADATA.find(scMeta => "TDRS 10".match(scMeta.match))?.icon.name}`,
        scale: SPACECRAFT_METADATA.find(scMeta => "TDRS 10".match(scMeta.match))?.icon.scale
      }),
      text: new Text({
        text: ["T171W", "bold 15px sans-serif"],
        offsetY: 25,
        fill: new Fill({color: 'green'}),
      })
    })
  },
  "TDRS 12": {
    name: "TDRS 12",
    description: "T041W",
    geometry: new Point([0, 0]),
    style: new Style({
      image: new Icon({
        anchor: [0.5, 0.5],
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        src: `img/icons/issmccmap/${SPACECRAFT_METADATA.find(scMeta => "TDRS 12".match(scMeta.match))?.icon.name}`,
        scale: SPACECRAFT_METADATA.find(scMeta => "TDRS 12".match(scMeta.match))?.icon.scale
      }),
      text: new Text({
        text: ["T041W", "bold 15px sans-serif"],
        offsetY: 25,
        fill: new Fill({color: 'yellow'}),
      })
    })
  },
  "zoe": {
    name: "ZOE",
    geometry: new Point(fromLonLat([74, 15])),
    style: new Style({
      text: new Text({
        text: [
          "ZOE", "bold 15px sans-serif",
          '\n', "",
          "Zone of exclusion", "normal 10px sans-serif"
        ],
        fill: new Fill({color: 'white'})
      })
    })
  },
  "iss-footprint": {
    name: "ISS footprint",
    geometry: new Polygon(circular([0, 0], 0, 64).getCoordinates().map(p => p.map(c => fromLonLat(c)))),
    style: new Style({
      stroke: new Stroke({color: 'white', width: 1})
    })
  },
  "TDRS 7-footprint": {
    name: "TDRS 7 footprint",
    description: "T275W footprint",
    geometry: new Polygon(
      circular([0, 0], 1, 64)
        .getCoordinates()
        .map(p => p.map(c => fromLonLat(c)))
    ),
    style: new Style({
      stroke: new Stroke({color: 'cyan', width: 1})
    })
  },
  "TDRS 10-footprint": {
    name: "TDRS 10 footprint",
    description: "T171W footprint",
    geometry: new Polygon(
      circular([0, 0], 1, 64)
        .getCoordinates()
        .map(p => p.map(c => fromLonLat(c)))
    ),
    style: new Style({
      stroke: new Stroke({color: 'green', width: 1})
    })
  },
  "TDRS 12-footprint": {
    name: "TDRS 12 footprint",
    description: "T041W footprint",
    geometry: new Polygon(
      circular([0, 0], 1, 64)
        .getCoordinates()
        .map(p => p.map(c => fromLonLat(c)))
    ),
    style: new Style({
      stroke: new Stroke({color: 'yellow', width: 1})
    })
  },
  "STPB": {
    name: "STPB",
    description: "Saint Petersburg? Groundstation",
    geometry: new Point(fromLonLat([30.3923, 59.8151])),
    style: new Style({
      image: new CircleStyle({
        fill: new Fill({color: 'yellow'}),
        radius: 1
      }),
      text: new Text({text: ["STPB", "bold 12px sans-serif"], offsetY: -10, fill: new Fill({color: 'yellow'})})
    })
  },
  "STPB-coverage": {
    name: "STPB",
    description: "STPB Coverage",
    geometry: new Polygon(
      circular([30.3923, 59.8151], 1750 * 1000, 64)
        .getCoordinates()
        .map(p => p.map(c => fromLonLat(c)))
    ),
    style: new Style({
      stroke: new Stroke({color: 'yellow', width: 1})
    })
  },
  "DJS": {
    name: "DJS",
    description: "Baikonour Groundstation",
    geometry: new Point(fromLonLat([63.3526, 45.8976])),
    style: new Style({
      image: new CircleStyle({
        fill: new Fill({color: 'yellow'}),
        radius: 1
      }),
      text: new Text({text: ["DJS", "bold 12px sans-serif"], offsetY: -10, fill: new Fill({color: 'yellow'})})
    })
  },
  "DJS-coverage": {
    name: "DJS",
    description: "DJS Coverage",
    geometry: new Polygon(
      circular([63.3526, 45.8976], 2315 * 1000, 64)
        .getCoordinates()
        .map(p => p.map(c => fromLonLat(c)))
    ),
    style: new Style({
      stroke: new Stroke({color: 'yellow', width: 1})
    })
  },
  "ULD": {
    name: "ULD",
    description: "Ulan-Ude Groundstation",
    geometry: new Point(fromLonLat([107.5891, 51.8349])),
    style: new Style({
      image: new CircleStyle({
        fill: new Fill({color: 'yellow'}),
        radius: 1
      }),
      text: new Text({text: ["ULD", "bold 12px sans-serif"], offsetY: -10, fill: new Fill({color: 'yellow'})})
    })
  },
  "ULD-coverage": {
    name: "ULD",
    description: "ULD Coverage",
    geometry: new Polygon(
      circular([107.5891, 51.8349], 2220 * 1000, 64)
        .getCoordinates()
        .map(p => p.map(c => fromLonLat(c)))
    ),
    style: new Style({
      stroke: new Stroke({color: 'yellow', width: 1})
    })
  },
  "USK": {
    name: "USK",
    description: "Kirovskiy? Groundstation",
    geometry: new Point(fromLonLat([133.4948, 45.0929])),
    style: new Style({
      image: new CircleStyle({
        fill: new Fill({color: 'yellow'}),
        radius: 1
      }),
      text: new Text({text: ["USK", "bold 12px sans-serif"], offsetY: -10, fill: new Fill({color: 'yellow'})})
    })
  },
  "USK-coverage": {
    name: "USK",
    description: "USK Coverage",
    geometry: new Polygon(
      circular([133.4948, 45.0929], 2015 * 1000, 64)
        .getCoordinates()
        .map(p => p.map(c => fromLonLat(c)))
    ),
    style: new Style({
      stroke: new Stroke({color: 'yellow', width: 1})
    })
  },
  "PPK": {
    name: "PPK",
    description: "Petropavlovsk-Kamchatsky Groundstation",
    geometry: new Point(fromLonLat([158.6480, 53.0445])),
    style: new Style({
      image: new CircleStyle({
        fill: new Fill({color: 'yellow'}),
        radius: 1
      }),
      text: new Text({text: ["PPK", "bold 12px sans-serif"], offsetY: -10, fill: new Fill({color: 'yellow'})})
    })
  },
  "PPK-coverage": {
    name: "PPK",
    description: "PPK Coverage",
    geometry: new Polygon(
      circular([158.6480, 53.0445], 2015 * 1000, 64)
        .getCoordinates()
        .map(p => p.map(c => fromLonLat(c)))
    ),
    style: new Style({
      stroke: new Stroke({color: 'yellow', width: 1})
    })
  }
}

export function setMCCElementLocation(name: string, location: Coordinate | Coordinate[][]) {
  if (MCC_ELEMENTS.hasOwnProperty(name) && MCC_ELEMENTS[name].hasOwnProperty("geometry")) {
    if (MCC_ELEMENTS[name].geometry.getType() === "Point") {
      (MCC_ELEMENTS[name].geometry as Point).setCoordinates(location);
    }
    if (MCC_ELEMENTS[name].geometry.getType() === "Polygon") {
      (MCC_ELEMENTS[name].geometry as Polygon).setCoordinates(location as Coordinate[][]);
    }
  }
}
